// src/services/storageBridge.js
import { db, storage } from "../firebase/firebase";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getDoc } from "firebase/firestore";

const IS_HYBRID = (import.meta.env.VITE_DATA_MODE || 'firebase').toLowerCase() === 'hybrid';

// * Helpers locaux
export const loadLocal = (key, fallback = null) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw || raw === "undefined" || raw === "null" || raw.startsWith("[object")) {
      console.warn("[warning] Clé corrompue dans localStorage:", key, raw);
      localStorage.removeItem(key);
      return fallback;
    }
    return JSON.parse(raw);
  } catch (e) {
    console.warn("[warning] Erreur JSON.parse sur", key, ":", e.message);
    localStorage.removeItem(key);
    return fallback;
  }
};

export const saveLocal = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.warn("[warning] Erreur lors de la sauvegarde locale:", e.message);
  }
};

// * Vérifie connexion réseau
const isOnline = () => typeof navigator !== "undefined" && navigator.onLine;

// * Helper: Calcule une signature stable pour détecter les changements
const computeSignature = (items) => {
  if (!Array.isArray(items) || items.length === 0) return "";
  return items
    .map((item) => {
      const id = item?.id || "";
      const ts = item?.clientUpdatedAt || item?.updatedAt || 0;
      return `${id}:${ts}`;
    })
    .sort()
    .join("|");
};

// * Helper: Extrait ou génère clientUpdatedAt stable (unix seconds)
const getClientUpdatedAt = (item) => {
  if (item?.clientUpdatedAt && typeof item.clientUpdatedAt === "number") {
    return item.clientUpdatedAt;
  }
  if (item?.updatedAt) {
    if (typeof item.updatedAt === "number") return Math.floor(item.updatedAt / 1000);
    if (typeof item.updatedAt === "object" && item.updatedAt.seconds) {
      return item.updatedAt.seconds;
    }
  }
  return Math.floor(Date.now() / 1000);
};

/* ==========================================================
   [repeat]  SYNCHRONISATION Firestore <-> LocalStorage
   ========================================================== */

// -> Envoie les données locales vers Firestore
export const syncToFirestore = async (uid, key, collectionName) => {
  if (!IS_HYBRID || !uid || !isOnline()) return;

  // Cas doc settings : users/{uid}/settings/{key}
  if (key.endsWith("_settings")) {
    const localObj = loadLocal(key, null);
    if (localObj && typeof localObj === "object" && !Array.isArray(localObj)) {
      // Calculer signature pour éviter writes inutiles
      const sigKey = `${key}_sync_sig`;
      const currentSig = JSON.stringify(localObj);
      const lastSig = localStorage.getItem(sigKey);

      if (currentSig === lastSig && lastSig !== "") {
        console.log(`[skip] SyncToFirestore (doc): ${key} unchanged (signature match)`);
        return;
      }

      const docRef = doc(db, `users/${uid}/settings/${key}`);
      const clientUpdatedAt = Math.floor(Date.now() / 1000);
      await setDoc(
        docRef,
        {
          ...localObj,
          updatedAt: serverTimestamp(),
          clientUpdatedAt,
        },
        { merge: true }
      );
      console.log("[up] SyncToFirestore (doc):", `users/${uid}/settings/${key}`);

      // Enregistrer signature après upload réussi
      localStorage.setItem(sigKey, currentSig);
    }
    return;
  }

  const localData = loadLocal(key, []);
  if (!Array.isArray(localData)) return;

  // Calculer signature pour éviter ping-pong
  const sigKey = `${key}_sync_sig`;
  const currentSig = computeSignature(localData);
  const lastSig = localStorage.getItem(sigKey);

  if (currentSig === lastSig && lastSig !== "") {
    console.log(`[skip] SyncToFirestore: ${key} unchanged (signature match)`);
    return;
  }

  const basePath = `users/${uid}/${collectionName}`;
  console.log("[up] SyncToFirestore:", basePath, localData.length, "éléments");

  for (const item of localData) {
    if (!item?.id) {
      console.warn(`[syncToFirestore] item sans id dans ${key}, skip`, item);
      continue;
    }
    try {
      const clientUpdatedAt = getClientUpdatedAt(item);
      const docRef = doc(collection(db, basePath), item.id);
      await setDoc(
        docRef,
        {
          ...item,
          synced: true,
          updatedAt: serverTimestamp(),
          clientUpdatedAt,
        },
        { merge: true }
      );
    } catch (err) {
      console.error(`[syncToFirestore] Erreur ${collectionName}:`, err);
    }
  }

  // Enregistrer la signature après upload réussi
  localStorage.setItem(sigKey, currentSig);
};

// -> Télécharge les données depuis Firestore
export const syncFromFirestore = async (uid, key, collectionName) => {
  // Déterminer le fallback correct selon le type de clé
  const fallback = key.endsWith("_settings") ? {} : [];

  if (!uid || !isOnline()) return loadLocal(key, fallback);
  if (!IS_HYBRID) return loadLocal(key, fallback);

  try {
    // * Cas 1 : settings (document unique dans /users/{uid}/settings/{key})
    if (key.endsWith("_settings")) {
      const docRef = doc(db, `users/${uid}/settings/${key}`);
      const snap = await getDoc(docRef);

      if (snap.exists()) {
        const data = snap.data();
        saveLocal(key, data);
        console.log(`[inbox] Download (doc) Firestore -> local: ${key}`, data);
        return data;
      } else {
        console.warn(`[warning] Aucun document Firestore trouvé pour ${key}`);
        return loadLocal(key, {});
      }
    }

    // * Cas 2 : collection (ex: modeles, dossiers, relations)
    const basePath = `users/${uid}/${collectionName}`;
    const colRef = collection(db, basePath);
    const snapshot = await getDocs(colRef);

    const remoteData = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      _source: "remote",
    }));

    const localData = loadLocal(key, []).map((d) => ({
      ...d,
      _source: "local",
    }));

    const merged = [];

    for (const remoteItem of remoteData) {
      const localItem = localData.find((x) => x.id === remoteItem.id);

      if (!localItem) {
        merged.push(remoteItem); // nouvel élément du cloud
      } else {
        // Préférer clientUpdatedAt pour comparaisons stables
        const localClientTime = localItem.clientUpdatedAt || 0;
        const remoteClientTime = remoteItem.clientUpdatedAt || 0;

        // Fallback sur updatedAt si clientUpdatedAt absent
        const localTime = localClientTime ||
          (typeof localItem.updatedAt === "object"
            ? localItem.updatedAt.seconds || 0
            : localItem.updatedAt || 0);
        const remoteTime = remoteClientTime ||
          (typeof remoteItem.updatedAt === "object"
            ? remoteItem.updatedAt.seconds || 0
            : remoteItem.updatedAt || 0);

        if (remoteTime > localTime) {
          merged.push({ ...remoteItem, _resolved: "fromCloud" });
        } else if (localTime > remoteTime) {
          merged.push({ ...localItem, _resolved: "fromLocal" });
        } else {
          merged.push({ ...remoteItem, _resolved: "equal" });
        }
      }
    }

    // [refresh] Ajoute les éléments locaux manquants
    for (const localItem of localData) {
      if (!merged.find((x) => x.id === localItem.id)) {
        merged.push({ ...localItem, _resolved: "localOnly" });
      }
    }

    saveLocal(key, merged);
    console.log(`[ok] Fusion terminée (${collectionName}): ${merged.length} éléments`);
    return merged;
  } catch (err) {
    console.error(`[syncFromFirestore] Erreur ${collectionName}:`, err);
    return loadLocal(key, fallback);
  }
};

/* ==========================================================
   [package]  STORAGE (fichiers Firebase)
   ========================================================== */

// -> Upload d'un fichier vers Storage sécurisé
export const uploadToUserStorage = async (uid, file, pathInUserFolder) => {
  if (!uid || !file) throw new Error("UID ou fichier manquant");
  const fullPath = `users/${uid}/${pathInUserFolder}`;
  const storageRef = ref(storage, fullPath);
  console.log("[outbox] Upload fichier:", fullPath);

  const snapshot = await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(snapshot.ref);

  return { storagePath: fullPath, fileURL: downloadURL };
};
